import { useToast } from '@cian/ui-kit';
import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
  addNewbuildingToComparison,
  type IComparisonActionResult,
  removeNewbuildingFromComparison,
} from 'shared/api/comparison';
import { setNewbuildingComparisonStatus } from 'shared/reducers/newbuildingInformation/actions';
import { isAuthenticatedUserSelector } from 'shared/selectors/isAuthenticatedUserSelector';
import { getNewbuildingComparisonStatus, getNewbuildingId } from 'shared/selectors/newbuildingHeader';
import { EComparisonStatus } from 'shared/types/newbuildingHeader';
import { useApplicationContext } from 'shared/utils/applicationContext';

import { CompareButtonOnboarding } from './CompareButtonOnboarding';
import { CompareButtonWithTooltip } from './CompareButtonWithTooltip';
import { CompareToastContent } from './CompareToastContent';
import { COMPARE_ENABLED_CONFIG } from './constants';
import { trackAddNewbuildingToComparison, trackRemoveNewbuildingFromComparison } from './tracking';
import { useCompareOnboarding } from './useCompareOnboarding';

const COMPARISON_TOAST_HIDE_DELAY = 5000;
const COMPARISON_ERROR_TEXT = 'Не удалось изменить сравнение. Попробуйте еще раз';

interface INewbuildingHeaderCompareButtonProps {
  withOnboarding?: boolean;
}

export const NewbuildingHeaderCompareButton: React.FC<INewbuildingHeaderCompareButtonProps> = ({
  withOnboarding = false,
}) => {
  const { config, httpApi, logger } = useApplicationContext();
  const dispatch = useDispatch();
  const newbuildingId = useSelector(getNewbuildingId) as number;
  const isAuthenticated = useSelector(isAuthenticatedUserSelector);
  const comparisonStatus = useSelector(getNewbuildingComparisonStatus);
  const toast = useToast({ placement: 'bottom-right', hideDelay: COMPARISON_TOAST_HIDE_DELAY, size: 'L' });
  const isCompareEnabled = config.get<boolean>(COMPARE_ENABLED_CONFIG) || false;

  const isAdded = comparisonStatus === EComparisonStatus.Added;
  const isVisible = isCompareEnabled && comparisonStatus != null && comparisonStatus !== EComparisonStatus.Unavailable;

  const { open, closeHandler } = useCompareOnboarding(withOnboarding && isVisible);

  const showComparisonToast = React.useCallback(
    (result: IComparisonActionResult) => {
      toast.open({
        type: result.success === false ? 'error' : 'success',
        content: <CompareToastContent text={result.text} comparisonUrl={result.comparisonUrl} />,
      });
    },
    [toast],
  );

  const handleClick = React.useCallback(async () => {
    const nextValue = !isAdded;

    if (nextValue && !isAuthenticated) {
      window.waitForLoginPopup?.();

      return;
    }

    dispatch(setNewbuildingComparisonStatus(nextValue ? EComparisonStatus.Added : EComparisonStatus.Available));

    try {
      const result = nextValue
        ? await addNewbuildingToComparison({ httpApi, logger, newbuildingId })
        : await removeNewbuildingFromComparison({ httpApi, logger, newbuildingId });

      showComparisonToast(result);

      if (result.success === false) {
        dispatch(setNewbuildingComparisonStatus(nextValue ? EComparisonStatus.Available : EComparisonStatus.Added));

        return;
      }

      if (nextValue) {
        trackAddNewbuildingToComparison(newbuildingId);
      } else {
        trackRemoveNewbuildingFromComparison(newbuildingId);
      }
    } catch (_error) {
      dispatch(setNewbuildingComparisonStatus(nextValue ? EComparisonStatus.Available : EComparisonStatus.Added));
      showComparisonToast({ success: false, text: COMPARISON_ERROR_TEXT });
    }
  }, [isAdded, isAuthenticated, httpApi, logger, newbuildingId, showComparisonToast]);

  if (!isVisible) {
    return null;
  }

  if (!withOnboarding || !open) {
    return <CompareButtonWithTooltip isAdded={isAdded} onClick={handleClick} />;
  }

  return (
    <CompareButtonOnboarding open={open} onClose={closeHandler}>
      <CompareButtonWithTooltip disabled={true} isAdded={isAdded} onClick={handleClick} />
    </CompareButtonOnboarding>
  );
};
